import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LoggedUserService } from '@core/services/admin/logged-user.service';
import { ProductService } from '@core/product/product.service';
import { PushService } from '@core/push/push.service';
import { ResetAppStateService } from '@core/services/reset-app-state.service';
import { ENVIRONMENTS } from '@environments/base-env';
import { environment } from '@environments/environment';
import { APP_SLUGS } from '@routes/routes.types';
import { LivechatPrefsService } from '@shared/features/livechat/services/livechat-prefs.service';
import { StorageManager } from '@shared/utils/StorageManager';
import { CookieOptions, CookieService } from 'ngx-cookie-service';
import { ToastService } from '@shared/services/toast.service';
import { finalize, map, retry } from 'rxjs/operators';
import {
    IForgotPayload,
    ILogInResponse,
    IResetPassword,
    ISignInRequest,
    ISignUpRequest,
    ISignUpResponse,
} from '../../core/models/admin/auth.types';
import { AccountService } from '../../core/services/admin/account.service';
import { MailboxService } from '../../core/services/admin/mailbox.service';
import { AUTH_SLUGS } from './auth.routes';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly cookie = inject(CookieService);
    private readonly toastr = inject(ToastService);
    private readonly accountS = inject(AccountService);
    private readonly mailboxS = inject(MailboxService);
    private readonly userS = inject(LoggedUserService);
    private readonly resetAppStateS = inject(ResetAppStateService);
    private readonly pushS = inject(PushService);
    private readonly productS = inject(ProductService);
    private isLoggingOut = false;

    loginPayload = signal({ email: '', password: '' });
    token = signal(''); // token set in cookie for normal user case. Set in state for disabled cookies use. Set in localStorage only in DEV mode
    isLoading = signal(false);

    /* Requests */

    getSignIn$(payload: ISignInRequest) {
        const url = `${environment.api_base}${environment.endpoints.auth.signin}`;
        return this.http.post<ILogInResponse>(url, payload, { withCredentials: true });
    }

    signIn(payload: ISignInRequest) {
        this.isLoading.set(true);
        this.loginPayload.set(payload);

        this.getSignIn$(payload)
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe({
                next: res => this.handleLoginSuccess(res),
                error: err => this.#handleLoginError(err),
            });
    }

    handleLoginSuccess(res: ILogInResponse, redirectUrl?: string, navigate: boolean = true) {
        this.setAuth(res);
        if (navigate) {
            this.router.navigateByUrl(redirectUrl || this.productS.homeUrl());
        }
        this.makeAccountReqs();
    }

    #handleLoginError(err: HttpErrorResponse) {
        const invalid = 'Invalid email or password';
        const generic = 'Please try again or contact support';

        if (err.status === 423) {
            this.sendVerificationCode();
            this.router.navigate(['/auth/verify']);
        } else if (err.status === 401) {
            this.toastr.error('Please try again', invalid);
        } else {
            this.toastr.error(generic);
        }
    }

    signUp(payload: ISignUpRequest) {
        const url = `${environment.api_base}${environment.endpoints.auth.signup}`;

        return this.http
            .post<ISignUpResponse>(url, payload, {
                observe: 'response',
            })
            .pipe(
                map((response: HttpResponse<any>) => {
                    if (response.status === 200) {
                        return response.body;
                    }
                })
            );
    }

    forgotPassword(payload: IForgotPayload) {
        const url = `${environment.api_base}${environment.endpoints.auth.forgot}`;

        return this.http
            .post<any>(url, payload, {
                observe: 'response',
            })
            .pipe(
                map((response: HttpResponse<any>) => {
                    if (response.status === 200) {
                        return response.body;
                    }
                })
            );
    }

    resetPassword(payload: IResetPassword) {
        const url = `${environment.api_base}${environment.endpoints.auth.reset}`;

        return this.http
            .post<ILogInResponse>(url, payload, {
                observe: 'response',
            })
            .pipe(
                map((response: HttpResponse<any>) => {
                    if (response.status === 200) {
                        return response.body;
                    }
                })
            );
    }

    sendVerificationCode() {
        const url = `${environment.api_base}${environment.endpoints.auth.sendCode}`;

        this.http.post(url, { email: this.loginPayload().email }).subscribe({
            next: () => this.toastr.success('A verification code has been sent to your email'),
            error: () => this.toastr.error("There's been a problem sending the verification code"),
        });
    }

    verifyCode(code: string) {
        const url = `${environment.api_base}${environment.endpoints.auth.verify}`;
        const payload = {
            email: this.loginPayload().email,
            code: code,
        };

        return this.http.post(url, payload).pipe(
            retry({
                count: 2,
                delay: 1000,
            })
        );
    }

    /** Get initial account data needed globally */
    private makeAccountReqs() {
        void this.pushS.syncOnStart();
        this.userS.getUserDetails();
        this.accountS.getUserAccounts();
        // TwinnyX reads the ecommerce account where it needs it (catalog, analytics); the
        // Instagram / Facebook channels are Twinny's, not this app's.
        this.mailboxS.getAndSetMailboxesReq();
    }

    /* Util methods */

    logout(goToLogin = true) {
        if (this.isLoggingOut) return;
        this.isLoggingOut = true;
        // Unregister this device's push subscription while the JWT is still
        // valid, otherwise the next user on this browser inherits the alerts.
        // Never blocks logout (bounded timeout inside).
        void this.pushS.disableForLogout().finally(() => {
            this.isLoggingOut = false;
            this.finishLogout(goToLogin);
        });
    }

    private finishLogout(goToLogin: boolean) {
        // Reset global state
        this.resetAppStateS.resetAppState();
        this.resetState();

        // Remove credentials
        LivechatPrefsService.removeCredentials();
        this.deleteCookie('auth.token');
        if (!environment.production) {
            StorageManager.clearSession();
        }

        // Navigate to login
        if (goToLogin) {
            this.router.navigateByUrl(`${APP_SLUGS.AUTH}/${AUTH_SLUGS.login}`);
        }
    }

    resetState() {
        this.token.set('');
        this.loginPayload.set({ email: '', password: '' });
        this.isLoading.set(false);
    }

    checkIsLoggedIn() {
        const isLogged = this.isUserLogged();
        if (!isLogged) return;
        this.makeAccountReqs();
    }

    isUserLogged() {
        return !!this.getUserToken();
    }

    deleteCookie(name: string) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }

    getUserToken() {
        if (environment.mode === ENVIRONMENTS.local)
            return StorageManager.getFromLocalStorage('auth.token');
        else return this.token() || this.cookie.get('auth.token');
    }

    private setAuth(res: ILogInResponse) {
        if (environment.mode === ENVIRONMENTS.local)
            StorageManager.setProperty('auth.token', res.access_token);
        this.cookie.set('auth.token', res.access_token, this.getCookieConfig()); // create a domain cookie
        this.token.set(res.access_token); // set token in state for disabled cookies use case
    }

    private getCookieConfig(): CookieOptions {
        return {
            sameSite: 'None',
            path: '/',
            secure: true,
        };
    }
}
